import React, { useEffect, useMemo, useState } from 'react';
import InsuranceCard from '@components/Patient/InsuranceCard';
import { useSubjectLanguageVariants } from '@features/user';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { getCoverageDisplayLabel, getCoverageOrderLabel } from '../store/hooks';
import type { PatientCoverage } from '../types';

import { CoverageStatusChip, formatCoverageWindow } from './CoverageStatusChip';

interface InsuranceHistorySectionProps {
  editable?: boolean;
  embedded?: boolean;
  history?: PatientCoverage[];
  initialEditorMode?: 'add' | 'edit';
  onAddClick?: () => void;
  onCreate: (coverage: PatientCoverage) => Promise<void>;
  onDelete: (coverage: PatientCoverage) => Promise<void>;
  onEditClick?: (coverage: PatientCoverage) => void;
  onOpen?: () => void;
  onRemoveClick?: (coverage: PatientCoverage) => void;
  onRemoveSubmitted?: (coverage: PatientCoverage) => void;
  onSaveSubmitted?: (mode: 'add' | 'edit', coverage: PatientCoverage) => void;
  onUpdate: (coverage: PatientCoverage) => Promise<void>;
  onWorkflowStateChange?: (state: {
    hasOpenForm: boolean;
    isDirty: boolean;
    isSaving: boolean;
  }) => void;
  patientId: string;
}

type CoverageFormErrors = Record<string, string>;

const emptyPastCoverage = (patientId: string): PatientCoverage => ({
  patient_insurance_coverage_id: '',
  patient_id: patientId,
  organization_id: '',
  coverage_order: 1,
  kind: 'insurance',
  relationship_to_subscriber: 'self',
  status: 'expired',
  consent_on_file: false,
  effective_date: null,
  termination_date: null,
});

function validateHistoryCoverage(
  coverage: PatientCoverage,
  mode: 'add' | 'edit',
): CoverageFormErrors {
  const errors: CoverageFormErrors = {};

  if (mode === 'add') {
    if (!coverage.payer_id && !coverage.payer_name) {
      errors.payer_name = 'Select a payer for this past plan';
    }
    if (!coverage.member_id?.trim()) {
      errors.member_id = 'Member/Policy ID is required for claims';
    }
    if (!coverage.effective_date) {
      errors.effective_date = 'Effective date is required for past insurance';
    }
    if (!coverage.termination_date) {
      errors.termination_date = 'Termination date is required for past insurance';
    }
  }

  if (
    coverage.effective_date &&
    coverage.termination_date &&
    coverage.termination_date < coverage.effective_date
  ) {
    errors.termination_date = 'Termination date must be on or after the effective date';
  }

  return errors;
}

/**
 * Mutable insurance-history list. Expired coverage remains claim-usable for a
 * date of service inside its window; cancelled coverage remains visible but is
 * deliberately excluded from claim selection.
 */
export const InsuranceHistorySection: React.FC<InsuranceHistorySectionProps> = ({
  editable = true,
  embedded = false,
  history = [],
  initialEditorMode,
  onAddClick,
  onCreate,
  onDelete,
  onEditClick,
  onOpen,
  onRemoveClick,
  onRemoveSubmitted,
  onSaveSubmitted,
  onUpdate,
  onWorkflowStateChange,
  patientId,
}) => {
  const { singular: subjectLabel } = useSubjectLanguageVariants();
  const [dialogMode, setDialogMode] = useState<'add' | 'edit' | null>(initialEditorMode ?? null);
  const [draft, setDraft] = useState<PatientCoverage | null>(() =>
    initialEditorMode === 'add' ? emptyPastCoverage(patientId) : null,
  );
  const [deleteTarget, setDeleteTarget] = useState<PatientCoverage | null>(null);
  const [errors, setErrors] = useState<CoverageFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const dialogTitle = dialogMode === 'add' ? 'Add past insurance' : 'Edit past insurance';
  const sortedHistory = useMemo(
    () =>
      [...history].sort((a, b) =>
        (b.termination_date ?? '').localeCompare(a.termination_date ?? ''),
      ),
    [history],
  );

  useEffect(() => {
    onWorkflowStateChange?.({
      hasOpenForm: dialogMode !== null || deleteTarget !== null,
      isDirty,
      isSaving: isSaving || isDeleting,
    });
  }, [deleteTarget, dialogMode, isDeleting, isDirty, isSaving, onWorkflowStateChange]);

  if (!editable && !history.length) return null;

  const openAddDialog = () => {
    onAddClick?.();
    setDraft(emptyPastCoverage(patientId));
    setErrors({});
    setIsDirty(false);
    setDialogMode('add');
  };

  const openEditDialog = (coverage: PatientCoverage) => {
    onEditClick?.(coverage);
    setDraft({ ...coverage });
    setErrors({});
    setIsDirty(false);
    setDialogMode('edit');
  };

  const closeEditor = () => {
    setDialogMode(null);
    setDraft(null);
    setErrors({});
    setIsDirty(false);
  };

  const saveCoverage = async () => {
    if (!draft || !dialogMode) return;

    const validationErrors = validateHistoryCoverage(draft, dialogMode);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    onSaveSubmitted?.(dialogMode, draft);
    setIsSaving(true);
    try {
      if (dialogMode === 'add') {
        await onCreate(draft);
      } else {
        await onUpdate(draft);
      }
      setDialogMode(null);
      setDraft(null);
      setErrors({});
      setIsDirty(false);
    } finally {
      setIsSaving(false);
    }
  };

  const requestDelete = (coverage: PatientCoverage) => {
    onRemoveClick?.(coverage);
    setDeleteTarget(coverage);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    onRemoveSubmitted?.(deleteTarget);
    setIsDeleting(true);
    try {
      await onDelete(deleteTarget);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const editorContent = (
    <Stack spacing={3}>
      <Alert severity="info" variant="outlined">
        Coverage marked usable for claims is selected automatically when a claim&apos;s date of
        service falls inside this window.
      </Alert>

      {draft ? (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Coverage position</InputLabel>
              <Select
                label="Coverage position"
                value={draft.coverage_order}
                onChange={event =>
                  setDraft(current => {
                    setIsDirty(true);
                    return current ? { ...current, coverage_order: event.target.value } : current;
                  })
                }
              >
                <MenuItem value={1}>Primary</MenuItem>
                <MenuItem value={2}>Secondary</MenuItem>
                <MenuItem value={3}>Tertiary</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 300, flex: 1 }}>
              <InputLabel>How claims should use this record</InputLabel>
              <Select
                label="How claims should use this record"
                value={draft.status === 'cancelled' ? 'cancelled' : 'expired'}
                onChange={event =>
                  setDraft(current => {
                    setIsDirty(true);
                    return current ? { ...current, status: event.target.value } : current;
                  })
                }
              >
                <MenuItem value="expired">Use within the coverage dates</MenuItem>
                <MenuItem value="cancelled">Cancelled — do not use for claims</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <InsuranceCard
            insurance={draft}
            onChange={coverage => {
              setDraft(coverage as PatientCoverage);
              setIsDirty(true);
            }}
            title="Past insurance details"
            patientId={patientId}
            errors={errors}
            disabled={isSaving}
          />
        </>
      ) : null}
    </Stack>
  );

  const editorActions = (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      <Button onClick={closeEditor} disabled={isSaving}>
        Cancel
      </Button>
      <Button variant="contained" onClick={() => void saveCoverage()} loading={isSaving}>
        {dialogMode === 'add' ? 'Save past insurance' : 'Save changes'}
      </Button>
    </Stack>
  );

  return (
    <>
      {embedded && dialogMode ? (
        <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
          {editorContent}
          <Box sx={{ mt: 2 }}>{editorActions}</Box>
        </Box>
      ) : (
        <Accordion
          disableGutters
          elevation={0}
          onChange={(_event, expanded) => {
            if (expanded) onOpen?.();
          }}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            '&:before': { display: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Insurance history ({history.length})
              </Typography>
            </AccordionSummary>
            {editable ? (
              <Button
                size="small"
                variant="text"
                startIcon={<AddIcon />}
                onClick={openAddDialog}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add past insurance
              </Button>
            ) : null}
          </Box>

          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1.5}>
              {!sortedHistory.length ? (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 2,
                    py: 2.5,
                    bgcolor: 'background.default',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No past insurance is on file. Add a plan here when a claim needs coverage that{' '}
                    {`predates the ${subjectLabel}’s current insurance.`}
                  </Typography>
                </Box>
              ) : (
                sortedHistory.map(coverage => {
                  const label = getCoverageDisplayLabel(coverage) ?? 'Unknown payer';
                  const window = formatCoverageWindow(coverage);
                  const isCancelled = coverage.status === 'cancelled';

                  return (
                    <Box
                      key={coverage.patient_insurance_coverage_id}
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.25,
                        border: 1,
                        borderColor: 'divider',
                        borderLeftWidth: 3,
                        borderLeftColor: isCancelled ? 'grey.400' : 'primary.light',
                        borderRadius: 1,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {label}
                          </Typography>
                          <CoverageStatusChip coverage={coverage} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {[
                            getCoverageOrderLabel(coverage.coverage_order),
                            coverage.member_id ? `Member ${coverage.member_id}` : null,
                            window,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                        {isCancelled && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            Not used for claims
                          </Typography>
                        )}
                      </Box>

                      {editable ? (
                        <Stack direction="row" spacing={0.25}>
                          <Tooltip title="Edit past insurance">
                            <IconButton
                              size="small"
                              aria-label={`Edit ${label}`}
                              onClick={() => openEditDialog(coverage)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove past insurance">
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Remove ${label}`}
                              onClick={() => requestDelete(coverage)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : null}
                    </Box>
                  );
                })
              )}

              <Typography variant="caption" color="text.secondary">
                Expired plans are used automatically for claims with a date of service inside their
                coverage window. Cancelled plans are kept for reference and are not used for claims.
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog
        open={!embedded && dialogMode !== null}
        onClose={closeEditor}
        fullWidth
        maxWidth="md"
        aria-labelledby="insurance-history-dialog-title"
      >
        <DialogTitle id="insurance-history-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent dividers>{editorContent}</DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>{editorActions}</DialogActions>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
        aria-labelledby="remove-history-dialog-title"
      >
        <DialogTitle id="remove-history-dialog-title">Remove past insurance?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Claims for dates in this coverage window will no longer use it automatically. This
            {` removes the plan from the ${subjectLabel}’s insurance record.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            Keep insurance
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmDelete()}
            loading={isDeleting}
          >
            Remove insurance
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
